import Header from '@/components/common/MainContentHeader';
import useI18n from '@/hooks/useI18n';
import { UserContext } from '@/context/UserContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/common/Button';
import { toast } from 'react-toastify';
import { useInView } from 'react-intersection-observer';
import { formatCampaignDate, formatTimeFromISO } from '@/utils/dateUtils';
import { formatMoney } from '@/utils/numberUtils';
import { getUsedPlatforms } from '@/utils/socialplatformUtils';
import Loading from '@/components/common/Loading';
import { useNavigate } from 'react-router-dom';
import { BrandMemberRole } from '@/types/enums';
import ToolTip from '@/components/common/ToolTip';
import {
  useGetBrandCampaignsInfiniteQuery,
  useUpdateCampaignMutation,
} from '@/hooks/queries/useCampaignQueries';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/queries/queryKeys';

const ScheduledCampaign = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const queryClient = useQueryClient();
  const [isActionLoading, setIsActionLoading] = useState(false);

  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user, refreshCampaignStats } = userContext;

  const getTitle = () => {
    if (user && user.brand) {
      return `${user.brand.name}'s`;
    }
    return '';
  };

  const isRegularBrandUser = useMemo(
    () => user?.brandMemberRole === BrandMemberRole.User,
    [user?.brandMemberRole],
  );

  const {
    data: campaignsData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useGetBrandCampaignsInfiniteQuery('scheduled');

  const scheduledCampaigns =
    campaignsData?.pages.flatMap((page: any) => page?.campaigns ?? []) ?? [];

  const updateMutation = useUpdateCampaignMutation();

  const publishScheduledCampaign = async (campaignId: string) => {
    setIsActionLoading(true);
    const isLastScheduledCampaign = scheduledCampaigns.length === 1;
    try {
      const response = await updateMutation.mutateAsync({
        id: campaignId,
        updateData: { status: 'live' },
      });
      if (response) {
        refreshCampaignStats();
        queryClient.invalidateQueries({
          queryKey: queryKeys.campaigns.byStatusInfinite('scheduled'),
        });
        if (isLastScheduledCampaign) {
          navigate('/active-campaign');
        }
      }
    } catch (error) {
      toast.error(t('wallet.errors.spending'));
    } finally {
      setIsActionLoading(false);
    }
  };

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const showLoading = isLoading || isActionLoading;
  const isInfiniteLoading = isFetchingNextPage;

  return (
    <>
      <div className="bg-white h-[calc(100%-152px)] overflow-visible">
        <div className="p-[24px] lg:bg-[#F0F5FF] bg-white">
          <Header
            title={getTitle()}
            subtitle={String(t('common.scheduledCampaign'))}
          />
        </div>
        {scheduledCampaigns.map((campaign) => {
          const startDate = formatCampaignDate(campaign.startDate, false);
          const endDate = formatCampaignDate(campaign.endDate ?? '', false);
          const startTime = formatTimeFromISO(campaign.startDate);
          const lastEdited = formatCampaignDate(
            campaign.updatedAt ?? Date.now().toLocaleString(),
            false,
          );
          const platform = getUsedPlatforms(campaign.containType);

          return (
            <div
              className="bg-white px-[12px] pt-[12px] sm:px-[24px] sm:pt-[24px] f"
              key={campaign._id}
            >
              <div className="border border-[#E5E9EB] rounded-3xl p-[24px] flex flex-col gap-[24px]">
                <div>
                  <div className="flex justify-between">
                    <span className="text-[#121111] text-lg font-normal capitalize">
                      {campaign.brandName}
                    </span>
                    <div className="flex gap-10 items-center">
                      <div>
                        <span className="text-[#9B919D] text-sm font-normal mr-5">
                          {t('common.lastEdited')}
                        </span>
                        <span className="text-[#000000B2]">{lastEdited}</span>
                      </div>
                      {!isRegularBrandUser && (
                        <ToolTip text={String(t('links.edit'))} position="top">
                          <button
                            onClick={() =>
                              navigate(`/edit-campaign/${campaign._id}`, {
                                state: { from: 'scheduled' },
                              })
                            }
                            className="hover:bg-gray-100 rounded-full p-1"
                          >
                            <img
                              src="assets/svgs/fi_edit.svg"
                              className="h-[16px] w-[16px]"
                              alt={String(t('campaign.navigation.editIcon'))}
                            />
                          </button>
                        </ToolTip>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex gap-[8px] items-center">
                      <img
                        src="/assets/fi_calendar.svg"
                        className="h-[16px] w-[16px]"
                        alt={String(t('campaign.navigation.calendarIcon'))}
                      />
                      <span className="text-[#000000B2]">
                        {startDate} -{' '}
                        {campaign.runCampaignTillMoneyEnd
                          ? t('common.untilBudgetEnds')
                          : endDate}
                      </span>
                    </div>
                    <div className="flex gap-[8px] items-center">
                      <img
                        src="/assets/fi_clock.svg"
                        className="h-[16px] w-[16px]"
                        alt={String(t('campaign.navigation.clockIcon'))}
                      />
                      <span className="text-[#000000B2]">{startTime}</span>
                    </div>
                    <span className="text-sm font-medium text-[#020D3A] border bg-[#020D3A1A] rounded-full px-[8px]">
                      {t('common.scheduled')}
                    </span>
                  </div>{' '}
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex gap-[32px]">
                    <div>
                      <div className="text-[#9B919D] font-normal text-sm mb-2">
                        {t('common.campaignBudget')}
                      </div>
                      <div className="text-lg font-normal text-[#292828]">
                        {formatMoney(campaign.campaignBudget)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[#9B919D] font-normal text-sm mb-2">
                        {t('common.campaignType')}
                      </div>
                      <div className="text-lg font-normal capitalize text-[#292828]">
                        {campaign.validFor[0]}
                      </div>
                    </div>
                    <div>
                      <div className="text-[#9B919D] font-normal text-sm mb-2">
                        {t('common.platform')}
                      </div>
                      <div className="flex flex-row gap-3 items-center">
                        {platform === 'both' || platform === 'tiktok' ? (
                          <img
                            src="assets/PlateformTikTok.svg"
                            className="w-[24px] h-[24px]"
                            alt={String(t('campaign.platforms.tiktokIcon'))}
                          />
                        ) : null}

                        {platform === 'both' || platform === 'instagram' ? (
                          <img
                            src="assets/PlateformInsta.svg"
                            className="w-[24px] h-[24px]"
                            alt={String(t('campaign.platforms.instagramIcon'))}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {!isRegularBrandUser && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        if (campaign._id) {
                          publishScheduledCampaign(campaign._id);
                        }
                      }}
                    >
                      {t('common.publishNow')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {showLoading && (
          <div className="flex justify-center items-center py-4">
            <Loading
              centeredToParent={true}
              style={{
                top: 'calc(50% + 60px)',
              }}
            />
          </div>
        )}

        {hasNextPage && !showLoading && (
          <div ref={sentinelRef} className="h-1" />
        )}
      </div>
      {isInfiniteLoading && (
        <div className="">
          <Loading className="!top-[97%] !left-[60%]" />
        </div>
      )}
    </>
  );
};

export default ScheduledCampaign;
